import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchTryOnResults, getTryOnDownloadUrl, getTryOnImageUrl } from '../../api/tryon';
import { useAuth } from '../../contexts/AuthContext';
import { useTabBarVisibility } from '../../contexts/TabBarVisibilityContext';
import { downloadImageUrlInBrowser, tryTelegramDownloadFile } from '../../utils/telegramFileDownload';
import './TryOnGalleryPage.css';

export default function TryOnGalleryPage() {
  const navigate = useNavigate();
  const { user, refreshUser, token } = useAuth();
  const tryonCredits = user?.tryon_credits ?? 0;

  useEffect(() => {
    refreshUser?.();
  }, [refreshUser]);
  const { setTabBarVisible } = useTabBarVisibility();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadNotice, setDownloadNotice] = useState(null);

  useEffect(() => {
    if (!downloadNotice) return undefined;
    const t = window.setTimeout(() => setDownloadNotice(null), 4000);
    return () => window.clearTimeout(t);
  }, [downloadNotice]);

  useEffect(() => {
    setTabBarVisible(false);
    const tg = window.Telegram?.WebApp;
    const backButton = tg?.BackButton;
    if (!backButton) {
      return () => {
        setTabBarVisible(true);
      };
    }
    const handleBack = () => {
      navigate(-1);
    };
    backButton.onClick(handleBack);
    backButton.show();
    return () => {
      backButton.offClick(handleBack);
      backButton.hide();
      setTabBarVisible(true);
    };
  }, [navigate, setTabBarVisible]);

  useEffect(() => {
    let cancelled = false;
    fetchTryOnResults(60)
      .then((data) => {
        if (!cancelled) setItems(data.items || []);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || 'Не удалось загрузить');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="tryon-gallery">
      <header className="tryon-gallery__header">
        <h1 className="tryon-gallery__title">Галерея примерок</h1>
        <p className="tryon-gallery__subtitle">
          Доступно примерок: <strong>{tryonCredits}</strong>
          {items.length > 0 && (
            <span className="tryon-gallery__subtitle-extra"> · на сервере: {items.length}</span>
          )}
        </p>
        {downloadNotice && (
          <p className="tryon-gallery__state tryon-gallery__state--err tryon-gallery__download-notice" role="status">
            {downloadNotice}
          </p>
        )}
      </header>

      {loading && <p className="tryon-gallery__state">Загрузка…</p>}
      {error && <p className="tryon-gallery__state tryon-gallery__state--err">{error}</p>}

      {!loading && !error && items.length === 0 && (
        <p className="tryon-gallery__empty">Пока нет сохранённых примерок — сгенерируй первую на вкладке «Примерка».</p>
      )}

      <div className="tryon-gallery__grid">
        {items.map((it) => (
          <article key={it.id} className="tryon-gallery__card">
            <div className="tryon-gallery__thumb-wrap">
              {it.id && token ? (
                <img
                  src={getTryOnImageUrl(it.id, token)}
                  alt=""
                  className="tryon-gallery__thumb"
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <div className="tryon-gallery__thumb-placeholder" />
              )}
            </div>
            <div className="tryon-gallery__meta">
              {it.created_at && (
                <time className="tryon-gallery__time" dateTime={it.created_at}>
                  {new Date(it.created_at).toLocaleString('ru-RU', {
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </time>
              )}
              <button
                type="button"
                className="tryon-gallery__download"
                disabled={downloadingId === it.id}
                onClick={async () => {
                  const url = getTryOnDownloadUrl(it.id);
                  const fileName = `matchwear-${it.id}.jpg`;
                  if (tryTelegramDownloadFile(url, fileName)) return;
                  setDownloadingId(it.id);
                  try {
                    const ok = await downloadImageUrlInBrowser(url, fileName);
                    if (!ok) {
                      setDownloadNotice('Не удалось сохранить файл. Попробуйте ещё раз.');
                    }
                  } finally {
                    setDownloadingId(null);
                  }
                }}
              >
                {downloadingId === it.id ? 'Сохранение…' : 'Скачать с водяным знаком'}
              </button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
